/**
 * Booking Audit Log Service
 *
 * Provides functions to insert and query audit log entries for bookings.
 * Used to track state transitions, field changes, and other booking modifications.
 *
 * change_type values: 'created', 'status_changed', 'payment_updated',
 *   'customer_info_updated', 'dates_changed', 'items_added', 'items_removed',
 *   'discount_applied', 'discount_removed', 'notes_added', 'cancelled',
 *   'refunded', 'inventory_reallocated', 'email_sent', 'other'
 */

import { supabase, handleSupabaseError } from '../lib/supabase';
import type { AccessControl } from '../lib/accessControl';
import type { Database } from '../lib/database.types';

type AuditLogRow = Database['public']['Tables']['booking_audit_log']['Row'];
type AuditLogInsert = Database['public']['Tables']['booking_audit_log']['Insert'];

export type AuditChangeType = AuditLogInsert['change_type'];

export interface AuditLogEntry {
  bookingId: string;
  merchantId: string;
  locationId?: string | null;
  changeType: string;
  changeDetails: Record<string, any>;
  userId?: string | null;
  userEmail?: string | null;
  triggeredBy?: string;
}

function toRecord(entry: AuditLogEntry): AuditLogInsert {
  return {
    booking_id: entry.bookingId,
    merchant_id: entry.merchantId,
    location_id: entry.locationId || null,
    change_type: entry.changeType,
    change_details: entry.changeDetails,
    user_id: entry.userId || null,
    user_email: entry.userEmail || null,
    triggered_by: entry.triggeredBy || 'admin_panel',
  };
}

/**
 * Insert a single audit log entry
 */
export async function insertAuditLog(entry: AuditLogEntry) {
  try {
    const { error } = await supabase
      .from('booking_audit_log')
      .insert(toRecord(entry));

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Failed to insert audit log:', error);
    return { error: handleSupabaseError(error) };
  }
}

/**
 * Insert multiple audit log entries at once (e.g. when multiple fields change in a single save)
 */
export async function insertAuditLogBatch(entries: AuditLogEntry[]) {
  if (entries.length === 0) return { error: null };

  try {
    const { error } = await supabase
      .from('booking_audit_log')
      .insert(entries.map(toRecord));

    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('Failed to insert audit log batch:', error);
    return { error: handleSupabaseError(error) };
  }
}

/**
 * Fetch audit log entries for a specific booking, newest first
 */
export async function getBookingAuditLog(
  bookingId: string,
  accessControl: AccessControl | null
) {
  try {
    let query = supabase
      .from('booking_audit_log')
      .select('*')
      .eq('booking_id', bookingId)
      .order('created_at', { ascending: false });

    // Access control is handled by RLS, but we add merchant filtering for safety
    if (accessControl && !accessControl.isPlatformAdmin && accessControl.merchantId) {
      query = query.eq('merchant_id', accessControl.merchantId);
    }

    const { data, error } = await query;

    if (error) throw error;

    return { data: data as AuditLogRow[], error: null };
  } catch (error) {
    return { data: null, error: handleSupabaseError(error) };
  }
}
